import { computed } from 'vue'
import { useNavItems } from '@/composables/useNavItems'
import { useSettingsMenuSections } from '@/composables/useSettingsMenuSections'
import { useOrdersTabs } from '@/composables/useOrdersTabs'
import { useCustomersTabs } from '@/composables/useCustomersTabs'
import { useReportsTabs } from '@/composables/useReportsTabs'
import { t } from '@/locales'

export interface IPageLookupItem {
  id: string
  label: string
  routeName: string
  section?: string
  isDev?: boolean
}

// Flattens the top-level nav items, the settings sub-menu, and the Orders/
// Customers sub-page tabs into one permission-filtered list of static/
// menu-level pages, for the page lookup palette (Ctrl/Cmd+Shift+K). All
// sources are already permission-filtered via canUser() internally, so no
// additional filtering is needed here.
export function usePageLookupItems() {
  const navItems = useNavItems()
  const settingsMenuSections = useSettingsMenuSections()
  const ordersTabs = useOrdersTabs()
  const customersTabs = useCustomersTabs()
  const reportsTabs = useReportsTabs()

  return computed<IPageLookupItem[]>(() => {
    const items: IPageLookupItem[] = []

    // "Customers" is kept unconditionally, like "Orders" — a general search
    // term the user might type even though it lands on the same place as
    // "Business accounts" today. Its three tabs are gated by one shared
    // permission (customers.view), so it's always all three reachable or
    // none — there's no case where showing the generic entry duplicates
    // exactly one specific one.
    //
    // "Settings" is different: each of its sub-pages is gated by its own
    // distinct permission, so "exactly one reachable" is a real, common
    // case (e.g. a lab-assistant who can only reach Translations). In that
    // case the generic entry would be a literal duplicate of the one real
    // page with no disambiguating value, so it's dropped — but kept when
    // more than one settings sub-page is reachable, same reasoning as
    // Customers.
    const settingsItemCount = settingsMenuSections.value.reduce(
      (sum, section) => sum + section.items.length,
      0
    )

    for (const item of navItems.value) {
      if (item.id === 'settings' && settingsItemCount <= 1) continue
      items.push({ id: item.path, label: item.title, routeName: item.path })
    }

    for (const section of settingsMenuSections.value) {
      for (const menuItem of section.items) {
        // "Rapporter" is a redirect shell (SettingsReports -> SettingsReportSettings)
        // with no page of its own, but it's kept in the index anyway: it's the
        // label the user sees in the Settings nav, and none of its four sub-tabs
        // ("Generelt"/"Skabeloner"/"Beskrivelser"/"Bilag") contain the word, so
        // dropping it left the whole Reports area unsearchable by name. Selecting
        // it lands on the same page as clicking the nav item, via the redirect.
        items.push({
          id: menuItem.routeName,
          label: menuItem.label,
          routeName: menuItem.routeName,
          section: section.title,
        })
      }
    }

    const ordersSectionLabel = t('main-menu.item.orders')
    for (const tab of ordersTabs.value) {
      items.push({
        id: tab.routeName,
        label: tab.label,
        routeName: tab.routeName,
        section: ordersSectionLabel,
      })
    }

    const customersSectionLabel = t('main-menu.item.customers')
    for (const tab of customersTabs.value) {
      items.push({
        id: tab.routeName,
        label: tab.label,
        routeName: tab.routeName,
        section: customersSectionLabel,
      })
    }

    const reportsSectionLabel = t('W: Rapporter')
    for (const tab of reportsTabs.value) {
      items.push({
        id: tab.routeName,
        label: tab.label,
        routeName: tab.routeName,
        section: reportsSectionLabel,
      })
    }

    return items
  })
}
